// NodeNodelistField.jsx

import React, { useMemo, useState } from 'react';

// Context
import { useNodeTypes } from 'context/NodeTypesContext';
import { useTheme } from 'context/ThemeContext';
import { useBase } from 'context/BaseContext';

// Utilities
import {
  getNodelistNodeTitle,
  getNodelistPrimaryKey,
  isNodelistAllowMultiple,
  normalizeNodelistKeys,
  normalizeNodelistValue,
} from 'utilities/NodelistField';
import { getNodeSubtitle } from 'utilities/NodeSubtitle';
import { getRelationDisplayLines } from 'utilities/RelationDisplayFields';
import { resolveNodeIcon } from 'utilities/resolveNodeIcon';

// Hooks
import useNodesByKeysQuery from 'hooks/useNodesByKeysQuery';
import useNodeEnumPills from 'node/hooks/useNodeEnumPills';
import useNodeOpenIntent from 'views/hooks/useNodeOpenIntent';
import useNodelistTargetOptions from 'node/form/fields/nodelist/useNodelistTargetOptions';
import useRelationQuickAdd from 'node/form/fields/nodelist/useRelationQuickAdd';

// CSS
import './NodeNodelistField.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Pill from 'components/pill/Pill';
import SelectInput from 'components/selectinput/SelectInput';
import TextInput from 'components/textinput/TextInput';
import NodeNodelistModal from 'node/form/fields/nodelist/NodeNodelistModal';
import RelationQuickAddModal from 'node/form/fields/nodelist/RelationQuickAddModal';

/**
 * Resolves muted display lines for a hydrated related node.
 *
 * @param {object|null|undefined} item
 * @param {object|null|undefined} field
 * @param {object|null|undefined} targetTypeDef
 * @returns {{ key: string, text: string }[]}
 */
const useRelationItemDisplayLines = (item, field, targetTypeDef) => useMemo(() => {
  const targetFields = Array.isArray(targetTypeDef?.schema?.fields)
    ? targetTypeDef.schema.fields
    : [];
  return getRelationDisplayLines(
    item,
    field?.displayFields,
    targetFields,
    item?.type || field?.targetType || targetTypeDef?.key || '',
  );
}, [field?.displayFields, field?.targetType, item, targetTypeDef]);

/**
 * NodeNodelistFieldMeta
 *
 * Muted metadata lines configured via Relation displayFields.
 *
 * @param {{ key: string, text: string }[]} lines
 * @returns {ReactNode}
 */
const NodeNodelistFieldMeta = ({ lines }) => {
  const { theme } = useTheme();
  const safeLines = Array.isArray(lines) ? lines : [];
  if (!safeLines.length) return null;

  return (
    <div className="node-nodelist-field-meta">
      {safeLines.map((line) => (
        <div
          className="node-nodelist-field-meta-line"
          key={line.key}
          style={{ color: theme.foregroundMuted }}
        >
          {line.text}
        </div>
      ))}
    </div>
  );
};

/**
 * NodeNodelistFieldItem
 *
 * Renders a hydrated node row inside the nodelist field.
 *
 * @param {object} item - Hydrated node row.
 * @param {object} field - Relation field definition.
 * @param {string} targetType - Target type for fallback icon resolution.
 * @param {function} onNavigate - Called when the open action is pressed.
 *
 * @returns {ReactNode} The rendered list row.
 */
const NodeNodelistFieldItem = ({ item, field, targetType, onNavigate }) => {
  const { getType } = useNodeTypes();
  const { theme } = useTheme();
  const enumPills = useNodeEnumPills({ node: item });

  const typeDef = useMemo(() => {
    const resolvedType = String(item?.type || targetType || '').trim();
    if (!resolvedType) return null;
    return getType(resolvedType) || null;
  }, [getType, item?.type, targetType]);

  const displayLines = useRelationItemDisplayLines(item, field, typeDef);

  const { iconKey, iconPath } = useMemo(
    () => resolveNodeIcon({ node: item, typeDef }),
    [item, typeDef],
  );

  return (
    <div className="node-nodelist-field-item">
      <div className="node-nodelist-field-item-main">
        <div className="node-nodelist-field-item-icon">
          {iconPath ? (
            <Icon
              path={iconPath}
              color={theme.foregroundMuted}
              width={20}
              height={20}
            />
          ) : (
            <Icon
              iconKey={iconKey}
              color={theme.foregroundMuted}
              width={20}
              height={20}
            />
          )}
        </div>

        <div className="node-nodelist-field-item-body">
          <div className="node-nodelist-field-title">
            {getNodelistNodeTitle(item)}
          </div>

          <NodeNodelistFieldMeta lines={displayLines} />

          {enumPills.length > 0 && (
            <div className="node-nodelist-field-pills">
              {enumPills.map((pill) => (
                <Pill
                  key={pill.key}
                  color={pill.color || theme.foregroundMuted}
                  compact
                  label={pill.label}
                  labelColor={theme.highlightForeground}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {typeof onNavigate === 'function' ? (
        <button
          className="node-nodelist-field-open"
          type="button"
          aria-label={`Open ${getNodelistNodeTitle(item)}`}
          onClick={(event) => onNavigate(event, item)}
        >
          <Icon
            iconKey="open_in_new"
            color={theme.foregroundMuted}
            width={24}
            height={24}
          />
        </button>
      ) : null}
    </div>
  );
};

/**
 * NodeNodelistSingleField
 *
 * Single-select Relation control: SelectInput of target records + open-in-new.
 * In readOnly / previewMode: show selected label; no select; open stays.
 */
const NodeNodelistSingleField = ({
  node,
  field,
  value,
  onChange,
  readOnly = false,
}) => {
  const { getType } = useNodeTypes();
  const { theme } = useTheme();
  const { activeBase } = useBase();
  const { openNode } = useNodeOpenIntent();

  const targetType = String(field?.targetType || '').trim();
  const baseKey = node?.baseKey || activeBase?.key || null;
  const selectedKey = getNodelistPrimaryKey(value);
  const targetTypeDef = useMemo(
    () => (targetType ? getType(targetType) || null : null),
    [getType, targetType],
  );

  const {
    options: targetOptions,
    nodesByKey,
    resolveOptionValue,
  } = useNodelistTargetOptions({
    targetType,
    baseKey,
    enabled: Boolean(!readOnly && targetType && baseKey),
  });

  const {
    nodes: selectedNodes,
  } = useNodesByKeysQuery(selectedKey ? [selectedKey] : [], {
    enabled: Boolean(selectedKey),
  });

  const selectedNode = useMemo(() => {
    if (!selectedKey) return null;
    if (nodesByKey.has(selectedKey)) return nodesByKey.get(selectedKey);
    const hydrated = Array.isArray(selectedNodes) ? selectedNodes[0] : null;
    if (hydrated) return hydrated;
    // Visitor Peek: fall back to GetNode relatedNodes.
    const related = Array.isArray(node?.relatedNodes) ? node.relatedNodes : [];
    return related.find((entry) => String(entry?.key || '').trim() === selectedKey) || null;
  }, [node?.relatedNodes, nodesByKey, selectedKey, selectedNodes]);

  const displayLines = useRelationItemDisplayLines(selectedNode, field, targetTypeDef);

  const selectOptions = useMemo(() => {
    const emptyOption = { value: '', label: '', iconKey: '', note: '' };
    const byValue = new Map(targetOptions.map((option) => [option.value, option]));

    // Preserve a stored value that is no longer in the active options list.
    if (selectedKey && !byValue.has(selectedKey)) {
      const typeDef = targetType ? getType(targetType) : null;
      const { iconKey } = resolveNodeIcon({ node: selectedNode, typeDef });
      byValue.set(selectedKey, {
        value: selectedKey,
        label: selectedNode ? getNodelistNodeTitle(selectedNode) : 'Unavailable',
        iconKey: iconKey || '',
        note: selectedNode ? (getNodeSubtitle(selectedNode) || '') : 'No longer available',
      });
    }

    return [emptyOption, ...byValue.values()];
  }, [getType, selectedKey, selectedNode, targetOptions, targetType]);

  const handleValueChange = async (nextValue) => {
    if (readOnly) return;
    const raw = String(nextValue || '').trim();
    if (!raw) {
      onChange?.([]);
      return;
    }
    try {
      const nextKey = await resolveOptionValue(raw);
      onChange?.(nextKey ? [nextKey] : []);
    } catch (error) {
      console.error('[NodeNodelistField] catalog adopt failed:', error);
    }
  };

  const {
    footerActionLabel,
    onFooterAction,
    selectOptions: quickAddOptions,
    quickAddOpen,
    setQuickAddOpen,
    quickAddInitialTitle,
    handleCreated,
  } = useRelationQuickAdd({
    readOnly,
    targetType,
    baseKey,
    options: selectOptions,
    onSelect: handleValueChange,
  });

  const selectedLabel = useMemo(() => {
    if (!selectedKey) return '';
    if (selectedNode) return getNodelistNodeTitle(selectedNode);
    const option = quickAddOptions.find((entry) => entry.value === selectedKey);
    return option?.label || 'Unavailable';
  }, [quickAddOptions, selectedKey, selectedNode]);

  const handleNavigate = (event) => {
    event.stopPropagation();
    if (!selectedNode?.key) return;
    openNode(selectedNode, { preferPeek: true });
  };

  return (
    <div className={`node-nodelist-field-container${readOnly ? ' is-readonly' : ''}`}>
      <div className="node-nodelist-field-single">
        <div className="node-nodelist-field-single-row">
          {readOnly ? (
            <TextInput
              hybrid
              value={selectedLabel}
              placeholder=""
              readOnly
              width="100%"
            />
          ) : (
            <SelectInput
              hybrid
              value={selectedKey}
              options={quickAddOptions}
              placeholder="Select item..."
              emptyText={targetType ? 'No items available' : 'Choose a target database in field settings'}
              showFilter={field?.showFilter === true}
              footerActionLabel={footerActionLabel}
              onFooterAction={onFooterAction}
              onValueChange={handleValueChange}
              width="100%"
            />
          )}

          {selectedKey && selectedNode ? (
            <button
              className="node-nodelist-field-open node-nodelist-field-open-single"
              type="button"
              aria-label={`Open ${getNodelistNodeTitle(selectedNode)}`}
              title={`Open ${getNodelistNodeTitle(selectedNode)}`}
              onClick={handleNavigate}
            >
              <Icon
                iconKey="open_in_new"
                color={theme.foregroundMuted}
                width={24}
                height={24}
              />
            </button>
          ) : null}
        </div>

        <NodeNodelistFieldMeta lines={displayLines} />
      </div>

      {!readOnly ? (
        <RelationQuickAddModal
          isOpen={quickAddOpen}
          setOpen={setQuickAddOpen}
          typeKey={targetType}
          baseKey={baseKey}
          parentNode={node}
          initialTitle={quickAddInitialTitle}
          onCreated={handleCreated}
        />
      ) : null}
    </div>
  );
};

/**
 * NodeNodelistMultiField
 *
 * Multi-select Relation control with modal picker.
 * In readOnly / previewMode: rows stay; no hover / picker; open stays.
 */
const NodeNodelistMultiField = ({
  node,
  field,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
}) => {
  const { theme } = useTheme();
  const { openNode } = useNodeOpenIntent();

  const [hovered, setHovered] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const nodeKeys = useMemo(() => normalizeNodelistKeys(value), [value]);
  const targetType = String(field?.targetType || '').trim();

  const {
    nodes: resolvedNodes,
    isLoading: loading,
  } = useNodesByKeysQuery(nodeKeys, {
    enabled: nodeKeys.length > 0,
  });

  const relatedByKey = useMemo(() => {
    const map = new Map();
    const related = Array.isArray(node?.relatedNodes) ? node.relatedNodes : [];
    related.forEach((entry) => {
      const key = String(entry?.key || '').trim();
      if (key) map.set(key, entry);
    });
    return map;
  }, [node?.relatedNodes]);

  const displayNodes = useMemo(() => {
    const byKey = new Map();

    (Array.isArray(resolvedNodes) ? resolvedNodes : []).forEach((entry) => {
      const key = String(entry?.key || '').trim();
      if (key) byKey.set(key, entry);
    });

    // Visitor Peek: hydrate from GetNode relatedNodes when Firestore reads aren't available.
    nodeKeys.forEach((key) => {
      if (byKey.has(key)) return;
      const related = relatedByKey.get(key);
      if (related) byKey.set(key, related);
    });

    return Array.from(byKey.values())
      .filter((entry) => {
        const key = String(entry?.key || '').trim();
        if (!key || !nodeKeys.includes(key)) return false;
        if (targetType && String(entry?.type || '').trim() !== targetType) return false;
        return true;
      })
      .sort((left, right) => getNodelistNodeTitle(left).localeCompare(getNodelistNodeTitle(right)));
  }, [nodeKeys, relatedByKey, resolvedNodes, targetType]);

  // Last resort when nothing hydrated: show keys so RO isn't blank.
  const fallbackNodes = useMemo(() => {
    if (!readOnly || displayNodes.length > 0 || loading) return [];
    return nodeKeys.map((key) => ({ key, title: key }));
  }, [displayNodes.length, loading, nodeKeys, readOnly]);

  const rows = displayNodes.length > 0 ? displayNodes : fallbackNodes;

  const handleOpen = () => {
    if (readOnly) return;
    setModalOpen(true);
  };

  const handleKeyDown = (event) => {
    if (readOnly) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setModalOpen(true);
    }
  };

  const handleNavigate = (event, item) => {
    event.stopPropagation();
    if (!item?.key) return;
    // Prefer hydrated nodes for open; stub-only fallbacks skip navigate.
    if (!item?.type && !displayNodes.some((entry) => entry.key === item.key)) return;
    openNode(item, { preferPeek: true });
  };

  return (
    <>
      <div className={`node-nodelist-field-container${readOnly ? ' is-readonly' : ''}`}>
        <div
          className={`node-nodelist-field-input${readOnly ? ' is-readonly' : ''}`}
          style={{
            backgroundColor: (!readOnly && hovered) ? theme.backgroundMuted : 'transparent',
            cursor: readOnly ? 'default' : undefined,
          }}
          role={readOnly ? undefined : 'button'}
          tabIndex={readOnly ? undefined : 0}
          onClick={readOnly ? undefined : handleOpen}
          onKeyDown={readOnly ? undefined : handleKeyDown}
          onMouseEnter={readOnly ? undefined : () => setHovered(true)}
          onMouseLeave={readOnly ? undefined : () => setHovered(false)}
        >
          {!loading && rows.length === 0 && (
            <div className="node-nodelist-field-empty">
              {readOnly ? '' : 'Select items...'}
            </div>
          )}

          {loading && (
            <div className="node-nodelist-field-empty">
              Loading items...
            </div>
          )}

          {rows.length > 0 && (
            <div className="node-nodelist-field-list">
              {rows.map((item) => (
                <NodeNodelistFieldItem
                  field={field}
                  item={item}
                  key={item.key}
                  targetType={targetType}
                  onNavigate={
                    displayNodes.some((entry) => entry.key === item.key)
                      ? handleNavigate
                      : undefined
                  }
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {!readOnly ? (
        <NodeNodelistModal
          fieldLabel={fieldLabel}
          isOpen={modalOpen}
          node={node}
          setOpen={setModalOpen}
          targetType={targetType}
          displayFields={field?.displayFields}
          value={nodeKeys}
          onChange={(nextKeys) => onChange?.(normalizeNodelistValue(nextKeys, { allowMultiple: true }))}
        />
      ) : null}
    </>
  );
};

/**
 * NodeNodelistField
 *
 * Displays Relation in single (SelectInput) or multi (modal) mode.
 * Supports readOnly / previewMode (visitor Peek).
 *
 * @param {object} node - The parent node record.
 * @param {object} field - The field definition object.
 * @param {string} fieldLabel - Label for the field.
 * @param {string[]|object[]} value - Current field value.
 * @param {function} onChange - Called with the next selected node key list.
 * @param {boolean} [readOnly=false] - Display-only; no picker/select.
 * @param {boolean} [previewMode=false] - Alias for readOnly (NodeField visitor path).
 * @returns {ReactNode} The rendered nodelist field.
 */
const NodeNodelistField = ({
  node,
  field,
  fieldLabel,
  value,
  onChange,
  readOnly = false,
  previewMode = false,
}) => {
  const isReadOnly = Boolean(readOnly || previewMode);

  if (!isNodelistAllowMultiple(field)) {
    return (
      <NodeNodelistSingleField
        field={field}
        node={node}
        value={value}
        onChange={onChange}
        readOnly={isReadOnly}
      />
    );
  }

  return (
    <NodeNodelistMultiField
      field={field}
      fieldLabel={fieldLabel}
      node={node}
      value={value}
      onChange={onChange}
      readOnly={isReadOnly}
    />
  );
};

NodeNodelistField.displayName = 'NodeNodelistField';

export default NodeNodelistField;
